import { useEffect, useMemo, useState } from 'react'
import { useAccount } from 'wagmi'
import styled from 'styled-components'
import {
  Modal,
  Text,
  Button,
  AutoRenewIcon,
  Link,
  Image,
  Flex,
  Box,
  Card,
  Heading,
  Grid,
  Toggle,
  Skeleton,
} from '@pancakeswap/uikit'
import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import useToast from 'hooks/useToast'
import useCatchTxError from 'hooks/useCatchTxError'
import BigNumber from 'bignumber.js'
import { ToastDescriptionWithTx } from 'components/Toast'
import { getFullDisplayBalance, getDecimalAmount } from 'utils/formatBalance'
import { DeserializedPool } from 'state/types'
import { updateUserBalance, updateUserPendingReward, updateUserStakedBalance } from 'state/pools'
import { useAppDispatch } from 'state'
import { BIG_TEN } from 'utils/bigNumber'
import useStakePool from '../../../hooks/useStakePool'
import useUnstakePool from '../../../hooks/useUnstakePool'
import { getGfImage } from 'utils/gfImagePreloader'

interface StakeModalProps {
  isBnbPool: boolean
  pool: DeserializedPool
  stakingTokenBalance: BigNumber
  stakingTokenPrice: number
  isRemovingStake?: boolean
  discountSave: BigNumber
  onDismiss?: () => void
  busdPrice: Number
  gfNfts: any[]
}

interface NFTCardProps {
  selected?: boolean
}

const NFTSkeleton = styled(Skeleton)`
  /* Desktop */
  min-width: 400px;
  min-height: 400px;

  /* Mobile */
  @media (max-width: 500px) {
    min-width: 0;
    min-height: 0;
    width: 95vw; /* Max 95% vom Bildschirm */
    // height: 95vw; /* Quadratisch */
  }
`

const NFTCard = styled.div<NFTCardProps>`
  position: relative;
  border: 2px solid ${({ selected }) => (selected ? '#00d1ff' : 'transparent')};
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: border 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Desktop */
  min-width: 400px;
  min-height: 400px;

  /* Mobile */
  @media (max-width: 500px) {
    min-width: 0;
    min-height: 0;
    width: 95vw; /* Max 95% vom Bildschirm */
    // height: 95vw; /* Quadratisch */
  }

  &:hover {
    border-color: #00d1ff88;
  }
`

const StyledLink = styled(Link)`
  width: 100%;
`

const NFTGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  justify-items: center;
  margin-bottom: 24px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const ModalContentWrapper = styled.div`
  max-height: 100vh;
  display: flex;
  flex-direction: column;
`

const Footer = styled.div`
  position: sticky;
  bottom: 0px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2;
`

const NFTModal = styled(Modal)`
  width: 100%;
  max-height: 90hv;
`

const RankPill = styled.div`
  position: absolute;
  top: 20px;
  right: 15px;

  background: linear-gradient(135deg, #0b1f33, #132f4a);
  color: #00d1ff;

  font-size: 12px;
  font-weight: 600;

  padding: 6px 10px;
  border-radius: 999px;

  border: 1px solid #00d1ff55;
  backdrop-filter: blur(4px);

  box-shadow: 0 0 6px #00d1ff22;
  z-index: 1;
`

const SelectedBadge = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 8px;
  left: 8px;

  width: 24px;
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: ${({ visible }) => (visible ? 'linear-gradient(135deg, #0b1f33, #132f4a)' : 'rgba(0,0,0,0.4)')};

  border: 1px solid ${({ visible }) => (visible ? '#00d1ff55' : '#rgba(0,0,0,0.4)')};

  color: #00d1ff;
  font-size: 16px;
  font-weight: bold;

  transition: all 0.2s ease;
`

const RARITY_MAP = {
  Shape: {
    Trillon: 20,
    Princess: 35,
    Round: 45,
  },
  Color: {
    Fire: 12.5,
    Ice: 12.5,
    Wind: 12.5,
    Earth: 12.5,
    Thunder: 12.5,
    Water: 12.5,
    Light: 12.5,
    Darkness: 12.5,
  },
  Clarity: {
    FL: 5,
    VVSI: 10,
    VSI: 15,
    SI: 30,
    I: 40,
  },
  Carat: {
    '1.25': 5,
    '1.1': 10,
    '1': 15,
    '0.9': 30,
    '0.75': 40,
  },
  Cut: {
    Excellent: 5,
    VeryGood: 10,
    Good: 15,
    Fair: 30,
    Poor: 40,
  },
}

const StakeModal: React.FC<StakeModalProps> = ({ isBnbPool, pool, isRemovingStake = false, onDismiss, gfNfts }) => {
  const { sousId, stakingToken, userData, stakingLimit, earningToken } = pool
  const { address: account } = useAccount()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { onStake } = useStakePool(sousId, isBnbPool)
  const { onUnstake } = useUnstakePool(sousId, pool.enableEmergencyWithdraw)
  const { toastSuccess } = useToast()
  const { fetchWithCatchTxError, loading: pendingTx } = useCatchTxError()
  const [percent, setPercent] = useState(1)
  const [buyAmount, setBuyAmount] = useState(1)
  const [stakeAmount, setStakeAmount] = useState(
    getFullDisplayBalance(
      new BigNumber(percent).times(pool.earningTokenPrice).times(BIG_TEN.pow(stakingToken.decimals)),
      stakingToken.decimals,
      0,
    ),
  )

  const fullDecimalStakeAmount = getDecimalAmount(new BigNumber(stakeAmount), stakingToken.decimals)

  const getTokenLink = stakingToken.address ? `/swap?outputCurrency=${stakingToken.address}` : '/swap'

  const handleStakeInputChange = (input: string) => {
    setStakeAmount(input)
    const amount =
      Math.floor(Number(input) / pool.earningTokenPrice) > 20 ? 20 : Math.floor(Number(input) / pool.earningTokenPrice)
    setBuyAmount(amount)
    setPercent(amount)
  }

  const handleConfirmClick = async () => {
    const receipt = await fetchWithCatchTxError(() => {
      return onStake('0', 0, pool.isNft, pool, selected)
    })
    if (receipt?.status) {
      if (isRemovingStake) {
        toastSuccess(
          `${t('Unstaked')}!`,
          <ToastDescriptionWithTx txHash={receipt.transactionHash}>
            {t('Your %symbol% earnings have also been harvested to your wallet!', {
              symbol: earningToken.symbol,
            })}
          </ToastDescriptionWithTx>,
        )
      } else {
        toastSuccess(
          `${t('Success')}!`,
          <ToastDescriptionWithTx txHash={receipt.transactionHash}>
            {t('Your %symbol% has been swapped for NFTs!', {
              symbol: stakingToken.symbol,
            })}
          </ToastDescriptionWithTx>,
        )
      }
      dispatch(updateUserStakedBalance(sousId, account))
      dispatch(updateUserPendingReward(sousId, account))
      dispatch(updateUserBalance(sousId, account))
      onDismiss?.()
    }
  }

  // const [gfTokens, setGfTokens] = useState([])
  const [selected, setSelected] = useState([]) // max 2 NFTs
  const [sortRarity, setSortRarity] = useState(true)
  const [page, setPage] = useState(1)
  // const [isLoading, setIsLoading] = useState(true)
  const isLoading = gfNfts.length === 0

  const pageSize = 20
  const totalPages = Math.ceil(gfNfts.length / pageSize)
  // const paginated = gfTokens.slice((page - 1) * pageSize, page * pageSize)
  const sortedAll = useMemo(() => {
    return [...gfNfts].sort((a, b) => {
      return sortRarity ? a.rank - b.rank : a.tokenId - b.tokenId
    })
  }, [gfNfts, sortRarity])

  const paginated = useMemo(() => {
    return sortedAll.slice((page - 1) * pageSize, page * pageSize)
  }, [sortedAll, page])

  const toggleSelect = (tokenId) => {
    setSelected((prev) => {
      if (prev.includes(tokenId)) {
        return prev.filter((id) => id !== tokenId)
      }
      if (prev.length === 2) return prev
      return [...prev, tokenId]
    })
  }

  const handlySortByToggle = () => {
    setSortRarity(!sortRarity)
  }

  const sortedTokens = [...paginated].sort((a, b) => {
    return sortRarity ? a.rank - b.rank : a.tokenId - b.tokenId
  })

  return (
    <NFTModal
      width="95%"
      title={t('Select 2 Gem Fighter NFTs')}
      onDismiss={onDismiss}
      headerBackground={theme.colors.gradients.cardHeader}
      style={{ maxWidth: '1500px' }}
      bodyPadding="0"
    >
      <ModalContentWrapper>
        <Flex padding={`24px`} alignItems={'center'} justifyContent={'space-between'} mb={2}>
          <Text bold>{t('Your NFTs')}:</Text>
          <Flex alignItems={'center'}>
            <Text marginRight={2}>Sort by:</Text>
            <Text bold mx={2} color={sortRarity ? 'textDisabled' : 'secondary'}>
              ID
            </Text>
            <Toggle
              id="toggle-sort-by-rarity-button"
              scale="sm"
              checked={sortRarity}
              onChange={handlySortByToggle}
            ></Toggle>
            <Text bold mx={2} color={!sortRarity ? 'textDisabled' : 'secondary'}>
              Rarity % (Low-High)
            </Text>
          </Flex>
        </Flex>

        <NFTGrid>
          {isLoading
            ? [...Array(pageSize)].map((_, i) => <NFTSkeleton key={i} width={400} height={400} />)
            : sortedTokens.map((nft) => (
                <NFTCard
                  key={nft.tokenId}
                  selected={selected.includes(nft.tokenId)}
                  onClick={() => toggleSelect(nft.tokenId)}
                >
                  <SelectedBadge visible={selected.includes(nft.tokenId)}>
                    {selected.includes(nft.tokenId) ? '✓' : ''}
                  </SelectedBadge>
                  <RankPill>Rarity {(nft.rankPercentASC * 100).toFixed(1)}%</RankPill>
                  <Text>GemFighter #{nft.tokenId}</Text>
                  <Image
                    src={getGfImage(nft.previewImage).src ?? ''}
                    width={400}
                    height={400}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                    }}
                  />
                </NFTCard>
              ))}
        </NFTGrid>

        {/* Pagination */}
        {gfNfts.length > pageSize && (
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              justifyContent: 'center',
              gap: 8,
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
              Prev
            </Button>
            <Text>
              {page} / {totalPages}
            </Text>
            <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        )}

        <Footer>
          <Flex justifyContent={'center'}>
            <Button
              isLoading={pendingTx}
              endIcon={pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
              onClick={handleConfirmClick}
              disabled={selected.length !== 2}
              padding="24px 24px"
            >
              {pendingTx ? t('Swapping') : t('Swap 2 NFTs')}
            </Button>
          </Flex>

          {selected.length > 0 && (
            <Flex justifyContent={'center'}>
              <Button variant="text" scale="xs" color="secondary" onClick={() => setSelected([])}>
                {t(`Deselect NFT${selected.length > 1 ? 's' : ''}`)}
              </Button>
            </Flex>
          )}

          {!isRemovingStake && !pool.isNft && (
            <StyledLink external href={getTokenLink}>
              <Button width="100%" variant="secondary">
                {t('Buy More %symbol%', { symbol: stakingToken.symbol })}
              </Button>
            </StyledLink>
          )}
        </Footer>
      </ModalContentWrapper>
    </NFTModal>
  )
}

export default StakeModal
